import React, { useEffect, useState } from "react";
import api, { formatApiError } from "../lib/api";
import { toast } from "sonner";
import { BarChart3, Eye, TrendingUp, Lock, Users } from "lucide-react";
import { Link } from "react-router-dom";

export default function PostAnalytics({ postId }) {
    const [data, setData] = useState(null);
    const [locked, setLocked] = useState(false);

    useEffect(() => {
        api.get(`/posts/${postId}/analytics`).then((r) => setData(r.data)).catch((e) => {
            if (e?.response?.status === 402) setLocked(true);
            else if (e?.response?.status !== 403) toast.error(formatApiError(e));
        });
    }, [postId]);

    if (locked) {
        return (
            <section className="mt-6 bg-white brut-static rounded-xl p-5" data-testid="analytics-locked">
                <div className="flex items-center gap-2 mb-3">
                    <BarChart3 className="w-5 h-5" />
                    <h3 className="display text-xl font-black">Post analytics</h3>
                </div>
                <div className="rounded-lg bg-[#FFE55C] border-2 border-[#0A0A0A] p-4 flex items-start gap-3">
                    <Lock className="w-5 h-5 mt-0.5" />
                    <div className="flex-1">
                        <div className="font-black">Premium feature</div>
                        <div className="text-sm font-medium text-[#0A0A0A]/80">
                            See views, rating trend, rating distribution, and top raters per post.
                        </div>
                    </div>
                    <Link to="/pricing" className="px-3 py-2 rounded-lg brut bg-[#0A0A0A] text-white font-bold text-sm">Upgrade</Link>
                </div>
            </section>
        );
    }

    if (!data) return null;
    const maxDistCount = Math.max(1, ...data.distribution.map((d) => d.count));

    return (
        <section className="mt-6 bg-white brut-static rounded-xl p-5" data-testid="analytics-panel">
            <div className="flex items-center gap-2 mb-4">
                <BarChart3 className="w-5 h-5" />
                <h3 className="display text-xl font-black">Post analytics</h3>
            </div>
            <div className="grid grid-cols-3 gap-2 mb-5">
                <StatCard icon={Eye} label="Views" value={data.views} />
                <StatCard icon={TrendingUp} label="Avg" value={data.avg_rating.toFixed(1)} />
                <StatCard icon={Users} label="Raters" value={data.rating_count} />
            </div>

            <div className="mb-5">
                <div className="text-xs font-black uppercase tracking-wider text-[#4A4A4A] mb-2">Rating distribution</div>
                <div className="flex items-end gap-1 h-32 border-2 border-[#0A0A0A] rounded-lg bg-[#FFFDF7] p-2" data-testid="analytics-distribution">
                    {data.distribution.map((d) => {
                        const h = Math.max(4, (d.count / maxDistCount) * 100);
                        const bg = d.rating <= 3 ? "bg-[#FFB8D1]" : d.rating <= 6 ? "bg-[#B8E1FF]" : "bg-[#FFE55C]";
                        return (
                            <div key={d.rating} className="flex-1 flex flex-col items-center gap-1">
                                <div className="text-[10px] font-bold text-[#4A4A4A]">{d.count || ""}</div>
                                <div className={`w-full ${bg} border-2 border-[#0A0A0A] rounded-t`} style={{ height: `${h}%` }} title={`${d.count} rated ${d.rating}`} />
                                <div className="text-[10px] font-black">{d.rating}</div>
                            </div>
                        );
                    })}
                </div>
            </div>

            <div>
                <div className="text-xs font-black uppercase tracking-wider text-[#4A4A4A] mb-2">Top raters</div>
                    {data.top_raters.length === 0 ? (
                        <div className="text-sm text-[#4A4A4A] font-medium italic">No ratings yet.</div>
                    ) : (
                        <ul className="space-y-1.5">
                            {data.top_raters.map((r) => (
                                <li key={`${r.username}-${r.rating}`} className="flex items-center justify-between text-sm font-medium" data-testid={`analytics-rater-${r.username}`}>
                                    <Link to={`/u/${r.username}`} className="font-bold hover:underline">@{r.username}</Link>
                                    <span className="font-black">{r.rating}/10</span>
                                </li>
                            ))}
                        </ul>
                    )}
            </div>
        </section>
    );
}

function StatCard({ icon: Icon, label, value }) {
    return (
        <div className="brut-inset bg-[#FFFDF7] rounded-lg p-3">
            <div className="flex items-center gap-1 text-[10px] font-black uppercase tracking-wider text-[#4A4A4A]">
                <Icon className="w-3 h-3" /> {label}
            </div>
            <div className="display text-2xl font-black mt-1">{value}</div>
        </div>
    );
}
